<template>
  <Layout>
    <div v-if="isSubmitting || isCompleted" class="white-glove">
      <div class="container">
        <h3 class="mb-2 text-black">
          YOU’RE
          <span class="font-bold">ALL SET.</span>
        </h3>
        <processing-spinner :saved="isCompleted" :saving="isSubmitting"></processing-spinner>
        <p class="mt-4">{{ message('success', stylist ) }}</p>
          <button @click="isCompleted = false" class="btn btn btn-primary">Done</button>
      </div>
    </div>
    <div v-else >
      <div class="container white-glove" v-if="client">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <h3 class="mb-0 text-black">{{ client.firstName }}'s Quotes</h3>
          <div class="d-flex align-items-center">
            <i
              class="fas fa-sync-alt text-secondary mr-2"
              :class="{ 'fa-spin': loadingQuotes }"
              @click="!loadingQuotes && reloadQuotes()"
              :title="loadingQuotes ? 'Refreshing quotes...' : 'Reload quotes'"
              style="cursor: pointer;"
            ></i>

          </div>
        </div>
        <h6
          v-if="includedItems && isFinishedAddingItems"
          class="text-black font-bold"
        >CONFIRM PICKUP DETAILS</h6>
        <div class="filter-bar mb-3 d-none d-md-block">
          <button
            class="btn btn-primary btn-pickup buttonWidth mr-2"
            :disabled="loadingQuotes"
            @click="$bvModal.show('addItem')"
          >
            <i
              :class="loadingQuotes ? 'fa fa-spinner fa-spin' : 'fa fa-plus'"
              class="mr-2"
            ></i>
            {{ loadingQuotes ? 'Loading Items...' : 'Add Item' }}
          </button>
          <button
            class="btn btn-primary mr-2"
            :disabled="isSharing"
            @click="shareQuote"
          >
            <span v-if="isSharing">
              Sending <i class="fa fa-spinner fa-spin"></i>
            </span>
            <span v-else>
              Share <i class="fa fa-share"></i>
            </span>
          </button>
          <button
            v-for="option in filterOptions"
            :key="option.value"
            class="btn btn-outline mr-2"
            :class="{ active: selectedStatus === option.value }"
            @click="selectedStatus = option.value"
          >
            {{ option.label }} ({{ option.count }})
          </button>
        </div>
        <div class="filter-dropdown mb-3 d-md-none">
          <select class="form-control" v-model="selectedStatus">
            <option
              v-for="option in filterOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }} ({{ option.count }})
            </option>
          </select>
        </div>

        <!-- Select All Button - Only show when a filter is selected -->
        <div v-if="selectedStatus !== 'all' && selectedStatus !== 'All'" class="mb-3">
          <button
            class="btn btn-primary"
            @click="selectAllFilteredItems"
            :disabled="!displayedItems.length"
          >
            <i class="fa fa-check-square mr-2"></i>
            Select All ({{ displayedItems.length }})
          </button>
        </div>
        <div class="quote-grid">
          <!-- Skeleton Loading Cards -->
          <template v-if="loadingQuotes">
            <div v-for="n in 3" :key="n" class="quote-card loading-card">
              <div class="row d-flex align-items-center flex-nowrap">
                <div class="mr-3">
                  <span class="placeholder-img pulse"></span>
                </div>
                <div class="quote-info">
                  <h6 class="text-gray font-bold mrgin0Px-bottom">
                    <span class="loading-placeholder" style="width:100px;"></span>
                  </h6>
                  <p class="text-gray">
                    <span class="loading-placeholder" style="width:80px;"></span>
                    <br />
                    <strong><span class="loading-placeholder" style="width:60px;"></span></strong>
                  </p>
                </div>
              </div>
            </div>
          </template>

          <!-- Actual Quote Cards -->
          <template v-else>
            <quote-card
              v-for="pendingItem in displayedItems"
              :key="pendingItem.id"
              :item="pendingItem"
              :placeholder-img-url="placeholderImgUrl"
              :is-finished-adding-items="isFinishedAddingItems"
              :is-clickable="isItemClickable(pendingItem)"
              @toggle-included="toggleIncluded"
              @view-details="showReviewResponse"
            />
          </template>
        </div>
        <div v-if="includedItems.length" class="totalItemsIncluded d-none d-md-block">
          <div class="d-flex align-items-center justify-content-between">
            <span>SELECTED QUOTES: {{ includedItems.length }}</span>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="deselectAllItems"
            >
              <i class="fa fa-times mr-1"></i>
              Deselect All
            </button>
          </div>
        </div>
        <div v-if="!isFinishedAddingItems" class="d-none d-md-block">
          <div class="d-flex align-items-center">
            <button
              type="button"
              class="btn btn-primary mr-2 ml-2"
              @click="getStatusActionMethod(selectedQuoteStatus)()"
              v-if="shouldShowStatusButton"
            >{{ selectedStatusButtonText }}</button>
          </div>
        </div>
        <b-modal v-if="client" id="addItem" scrollable title-class="font-18" footer-class="d-none">
          <template v-slot:modal-header>
            <div class="close-modal">
          <i class="bx bx-x" @click="$bvModal.hide('addItem')"></i>
        </div>
          </template>
          <new-item @addedItem="addedItem" v-if="brands" :brands="brands" :clientId="client.id"></new-item>
        </b-modal>
        <b-modal id="acceptedQuoteActions" title="Quote Actions" title-class="font-18" size="xl">
          <template v-slot:modal-header>
            <div class="close-modal">
              <i class="bx bx-x" @click="$bvModal.hide('acceptedQuoteActions')"></i>
            </div>
          </template>

          <div class="accepted-quote-actions-content">
            <p class="mb-3">Select an action for the accepted quote(s):</p>
            <div class="selected-quotes-list mb-4">
              <div
                v-for="item in includedItems"
                :key="item.id"
                class="quote-item mb-2 p-2 border rounded"
              >
                <div class="d-flex align-items-center">
                  <div class="item-thumbnail mr-3">
                    <img
                      :src="getItemThumbnail(item)"
                      :alt="item.attributes.name"
                      class="thumbnail-img"
                      @error="handleImageError"
                    />
                  </div>
                  <div class="item-details flex-grow-1">
                    <strong>{{ item.attributes.name }}</strong>
                    <div class="text-muted small">{{ item.attributes.brand }} - {{ item.attributes.category }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="action-buttons">
              <button
                type="button"
                class="btn btn-primary btn-lg mb-3 w-100"
                @click="handleShippingLabel()"
              >
                <i class="fa fa-tag mr-2"></i>
                Shipping Label
              </button>
              <button
                type="button"
                class="btn btn-primary btn-lg w-100"
                @click="showPickupSignature()"
              >
                <i class="fa fa-truck mr-2"></i>
                Pickup
              </button>
            </div>
          </div>
        </b-modal>

        <!-- Pickup Signature Modal -->
        <b-modal
          id="pickupSignature"
          title="Pickup Confirmation"
          title-class="font-18"
          size="xl"
          @hidden="onSignatureModalHidden"
        >
          <template v-slot:modal-header>
            <div class="close-modal">
              <i class="bx bx-x" @click="$bvModal.hide('pickupSignature')"></i>
            </div>
          </template>

          <div class="pickup-signature-content">
            <div class="selected-quotes-summary mb-4">
              <h6 class="font-weight-bold mb-3">Items to be picked up:</h6>
              <div
                v-for="item in includedItems"
                :key="item.id"
                class="quote-item mb-2 p-2"
              >
                <div class="d-flex align-items-center">
                  <div class="item-thumbnail mr-3">
                    <img
                      :src="getItemThumbnail(item)"
                      :alt="item.attributes.name"
                      class="thumbnail-img"
                      @error="handleImageError"
                    />
                  </div>
                  <div class="item-details flex-grow-1">
                    <strong>{{ item.attributes.name }}</strong>
                    <div class="text-muted small">{{ item.attributes.brand }} - {{ item.attributes.category }}</div>
                  </div>
                </div>
              </div>
            </div>

            <div class="signature-section">
              <h6 class="font-weight-bold mb-3">Client Signature Required:</h6>
              <p class="text-muted mb-3">Please have the client sign below to confirm pickup:</p>
              <div class="signature-pad-container">
                <signature-pad
                  v-if="showSignaturePad"
                  :key="signaturePadKey"
                  :signature="signature"
                  :width="550"
                  :height="300"
                  @mounted="onSignaturePadMounted"
                  @signatureData="signatureAdded"
                />
                <div v-else class="signature-pad-loading">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading signature pad...</span>
                  </div>
                  <p class="mt-2 text-muted">Initializing signature pad...</p>
                </div>
              </div>
            </div>
            <div>
              <b-form-checkbox
                id="generateShippingLabel"
                v-model="generateShippingLabel"
                switch
                class="mb-3"
              >
                Generate Shipping Label
              </b-form-checkbox>
            </div>

            <div class="pickup-confirmation-text mb-4">
              <div class="alert alert-info">
                <i class="fa fa-info-circle mr-2"></i>
                <strong>Pickup Confirmation:</strong>
                <p class="mb-0 mt-2">
                  By signing above, the client confirms that they have received the selected items
                  and acknowledges that these items are now marked as "Picked Up" in our system.
                </p>
              </div>
            </div>
          </div>

          <template v-slot:modal-footer>
            <div class="d-flex justify-content-between w-100">
              <button
                type="button"
                class="btn btn-secondary"
                @click="$bvModal.hide('pickupSignature')"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="!signature || loadingPickup"
                @click="confirmPickup()"
              >
                <span v-if="loadingPickup">
                  <i class="fa fa-spinner fa-spin mr-2"></i>
                  Confirming Pickup...
                </span>
                <span v-else>
                  <i class="fa fa-check mr-2"></i>
                  Confirm Pickup
                </span>
              </button>
            </div>
          </template>
        </b-modal>

        <!-- Review Response Modal -->
        <b-modal
          id="reviewResponse"
          title="Review Response & Offers"
          title-class="font-18"
          size="xl"
          @hidden="onReviewResponseModalHidden"
        >
          <template v-slot:modal-header>
            <div class="close-modal">
              <i class="bx bx-x" @click="$bvModal.hide('reviewResponse')"></i>
            </div>
          </template>

          <div class="review-response-content" v-if="acceptQuote">
            <div class="selected-quotes-summary mb-4">
              <h6 class="font-weight-bold mb-3">Details: #{{ acceptQuote.attributes.id }}</h6>
              <div
                class="quote-offer-item"
              >
                <div class="d-flex align-items-center mb-3">
                  <div class="item-thumbnail mr-3">
                    <img
                      :src="getItemThumbnail(acceptQuote)"
                      :alt="acceptQuote.attributes.name"
                      class="thumbnail-img"
                      @error="handleImageError"
                    />
                  </div>
                  <div class="item-details flex-grow-1">
                    <strong>{{ acceptQuote.attributes.brand }} - {{ acceptQuote.attributes.category }}</strong>
                    <div class="small">{{ acceptQuote.attributes.name }}</div>
                    <div class="small" v-if="acceptQuote.attributes.offers && acceptQuote.attributes.offers.acceptedOfferType">
                      Customer Response: {{ acceptQuote.attributes.offers.acceptedOfferType}}
                    </div>
                    <div class="small" v-if="acceptQuote.attributes.commenter">
                      <i class="fa fa-comment"></i> from {{ acceptQuote.attributes.commenter}}: {{ acceptQuote.attributes.commentToCustomer}}
                    </div>
                    <div class="meta">
                      <a
                        v-if="acceptQuote.attributes.shipment && acceptQuote.attributes.shipment.shiplistPDFLink"
                        :href="acceptQuote.attributes.shipment.shiplistPDFLink"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <span class="chip">
                          <i class="fa fa-truck mr-1"></i> Shiplist
                        </span>
                      </a>
                    </div>
                  </div>
                </div>

                <div class="offers-section" v-if="acceptQuote.attributes.isApproved">
                  <h6 class="font-weight-bold mb-2">Available Offers:</h6>
                  <div class="offers-list" v-if="getItemOffers(acceptQuote).length > 0">
                    <div
                      v-for="offer in getItemOffers(acceptQuote)"
                      :key="offer.type"
                      class="offer-item mb-2 p-2 border rounded"
                      :class="{ 'offer-disabled': isOfferDisabled(acceptQuote, offer.type) }"
                    >
                      <div class="offer-layout">
                        <div class="d-flex align-items-start justify-content-between mb-2">
                          <div class="offer-info">
                            <strong>{{ formatOfferType(offer.type) }}</strong>
                          </div>
                          <div v-if="offer.amount" class="offer-amount text-success font-weight-bold">
                            ${{ offer.amount }}
                          </div>
                        </div>
                        <div class="offer-actions">
                          <div class="btn-group" role="group">
                            <button
                              type="button"
                              class="btn btn-sm mr-2"
                              :class="getOfferButtonClass(acceptQuote.id, offer.type, 'accept')"
                              @click="onOfferSelectionChange(acceptQuote.id, offer.type, 'accept')"
                              :disabled="isOfferDisabled(acceptQuote, offer.type, 'accept')"
                            >
                              <i class="fa fa-check mr-1"></i>
                              Accept
                            </button>
                            <button
                              type="button"
                              class="btn btn-sm"
                              :class="getOfferButtonClass(acceptQuote.id, offer.type, 'reject')"
                              @click="onOfferSelectionChange(acceptQuote.id, offer.type, 'reject')"
                              :disabled="isOfferDisabled(acceptQuote, offer.type, 'reject')"
                            >
                              <i class="fa fa-times mr-1"></i>
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-offers-message">
                    <div class="alert alert-warning">
                      <i class="fa fa-exclamation-triangle mr-2"></i>
                      <strong>No offers found for this item.</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="response-summary mb-4">
              <div class="alert alert-info">
                <i class="fa fa-info-circle mr-2"></i>
                <strong>Response Summary:</strong>
                <p class="mb-0 mt-2">
                  Review all offers for each quote. You can only accept one offer per quote.
                  Once you submit, the accepted offers will be processed and quotes will be updated.
                </p>
              </div>
            </div>
          </div>

          <template v-slot:modal-footer>
            <div class="d-flex justify-content-between w-100">
              <button
                type="button"
                class="btn btn-secondary"
                @click="$bvModal.hide('reviewResponse')"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!hasValidResponses || submittingResponses"
                @click="submitResponses()"
              >
                <span v-if="submittingResponses">
                  <i class="fa fa-spinner fa-spin mr-2"></i>
                  Submitting Responses...
                </span>
                <span v-else>
                  <i class="fa fa-paper-plane mr-2"></i>
                  Submit Responses
                </span>
              </button>
            </div>
          </template>
        </b-modal>

        <!-- Shipping Label Options Modal -->
        <b-modal
          id="shippingLabelOptions"
          :title="`Shipping Label Options`"
          title-class="font-18"
          size="lg"
          @hidden="clearShippingLabelErrors"
        >
          <template v-slot:modal-header>
            <div class="d-flex justify-content-between align-items-center w-100">
              <div class="d-flex align-items-center">
                <i class="fa fa-tag mr-2"></i>
                <span>Create Shipping Label</span>
              </div>
              <div class="close-modal">
                <i class="bx bx-x" @click="cancelShippingLabelModal"></i>
              </div>
            </div>
          </template>

          <div class="shipping-label-options-content">
            <!-- Error Alert Section -->
            <div v-if="shippingLabelErrors.length > 0" class="error-alert mb-4">
              <div class="alert alert-danger">
                <div class="d-flex align-items-center mb-2">
                  <i class="fa fa-exclamation-triangle mr-2"></i>
                  <strong>Please fix the following errors:</strong>
                </div>

                <!-- General Errors -->
                <ul v-if="shippingLabelErrors.length > 0" class="mb-2">
                  <li v-for="(error, index) in shippingLabelErrors" :key="`general-${index}`">
                    {{ error }}
                  </li>
                </ul>
              </div>
            </div>

            <div class="selected-quotes-summary mb-4">
              <h6 class="font-weight-bold mb-2">Selected Quotes:</h6>
              <div class="selected-quotes-list">
                <div
                  v-for="item in includedItems"
                  :key="item.id"
                  class="quote-item mb-2 p-2 border rounded"
                >
                  <strong>{{ item.attributes.name }}</strong>
                  <div class="text-muted small">{{ item.attributes.brand }} - {{ item.attributes.category }}</div>
                </div>
              </div>
            </div>

            <div class="form-group form-check mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="emailClientShiplist"
                v-model="emailClientShiplist"
              />
              <label for="emailClientShiplist">
                Email {{ client ? client.firstName : 'client' }} the shiplist
              </label>
            </div>

            <div class="form-actions d-flex justify-content-end gap-2">
              <button
                type="button"
                class="btn btn-secondary mr-2"
                @click="cancelShippingLabelModal"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary btn-outline"
                @click="createShippingLabels"
                :disabled="isCreatingLabels"
              >
                <span v-if="isCreatingLabels">
                  <i class="fa fa-spinner fa-spin mr-2"></i>
                  Creating Label...
                </span>
                <span v-else>
                  <i class="fa fa-tag mr-2"></i>
                  Create Shipping Label
                </span>
              </button>
            </div>
          </div>
        </b-modal>
        <div v-if="createdLabel" class="success-message mt-4">
          <div class="alert alert-success">
            <h6 class="alert-heading">
              <i class="fa fa-check-circle mr-2"></i>
              Shipping Labels Created Successfully!
            </h6>
            <p class="mb-3">Your shipping labels have been generated. You can download them below:</p>

            <div class="labels-list">
              <div
                class="label-item d-flex align-items-center p-3 border rounded mb-2"
              >
                <div class="flex-grow-1">
                  <div class="small text-muted">
                    Tracking: <code>{{ createdLabel.tracking_number }}</code>
                  </div>
                  <div class="small text-muted">
                    {{ createdLabel.shipping_method }} • {{ createdLabel.warehouse }}
                  </div>
                </div>
                <div class="label-actions">
                  <a
                    :href="createdLabel.label_url"
                    target="_blank"
                    class="btn btn-sm btn-primary mr-2"
                    title="View label"
                  >
                    <i class="fa fa-eye mr-1"></i>
                    View Label
                  </a>
                  <a
                    :href="createdLabel.shiplistPDFLink"
                    download
                    target="_blank"
                    class="btn btn-sm btn-primary"
                    title="Download label"
                  >
                    <i class="fa fa-download mr-1"></i>
                    Shiplist
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Action Bar -->
    <div class="mobile-action-bar d-md-none" v-if="client && !isSubmitting && !isCompleted">
      <!-- Show only Add Item button when nothing is selected -->
      <div v-if="includedItems.length === 0" class="action-bar-content">
        <button
          class="btn btn-dark action-btn-full"
          @click="$bvModal.show('addItem')"
        >
          <i class="fa fa-plus"></i>
          Add Item
        </button>
      </div>

      <!-- Show status-specific button when items are selected -->
      <div v-else class="action-bar-content">
        <div class="mobile-selected-count">
          <div class="d-flex align-items-center justify-content-between">
            <span>SELECTED QUOTES: {{ includedItems.length }}</span>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm"
              @click="deselectAllItems"
            >
              <i class="fa fa-times mr-1"></i>
              Deselect All
            </button>
          </div>
        </div>
        <div class="mobile-action-buttons">
          <button
            class="btn btn-dark action-btn-compact"
            @click="$bvModal.show('addItem')"
            v-if="!isFinishedAddingItems"
          >
            <i class="fa fa-plus"></i>
          </button>

          <button
            type="button"
            class="btn btn-primary action-btn-status"
            @click="getStatusActionMethod(selectedQuoteStatus)()"
            v-if="shouldShowStatusButton"
          >
            {{ selectedStatusButtonText }}
          </button>

          <button
            class="btn btn-outline-secondary action-btn"
            @click="isFinishedAddingItems = false"
            v-if="isFinishedAddingItems"
          >
            <i class="fa fa-undo"></i>
            <span class="btn-text">Reset</span>
          </button>

        </div>
      </div>
    </div>

  </Layout>
</template>
<script>
import api from '@/services/api'
import shippingLabelService from '@/services/shipping-label'
import Layout from '@/router/layouts/main'
import NewItem from '@/components/shared/new-item'
import QuoteCard from '@/components/shared/quote-card'
import SignaturePad from '@/components/signature-pad'
import ProcessingSpinner from '@/components/processing-spinner'

export default {
  name: 'pick-up',
  components: {
    ProcessingSpinner,
    SignaturePad,
    NewItem,
    QuoteCard,
    Layout
  },
  data () {
    return {
      placeholderImgUrl: require('@/assets/images/knot.svg'),
      newItem: {},
      placeholder: 'Search Clients',
      brands: [],
      isFinishedAddingItems: false,
      signature: '',
      isCompleted: false,
      isSubmitting: false,
      stylist: this.$store.state.stylist,
      hasExistingQuotes: false,
      errorMessages: '',
      loadingQuotes: false,
      isFetchingQuotes: false,
      currentClient: null,
      selectedStatus: 'All',
      isDeleting: false,
      isCreatingLabels: false,
      // Whether to email the shiplist to the client when creating labels
      emailClientShiplist: false,
      loadingPickup: false,
      shippingLabelErrors: [],
      showSignaturePad: false,
      signaturePadKey: 0,
      itemResponses: {},
      submittingResponses: false,
      createdLabel: '',
      acceptQuote: '',
      isSharing: false,
      shareMessage: '',
      generateShippingLabel: false,
      actionableStatuses: [
        'Accepted'
      ],
      statusMetadata: {
        pending_authentication: {
          label: 'Pending Authentication',
          description: 'Item is being reviewed for authenticity',
          stylistAction: 'Wait for admin review',
          clientAction: 'None',
          color: 'warning',
          icon: 'clock',
          priority: 8
        },
        authentication_in_progress: {
          label: 'Authentication in Progress',
          description: 'Item is under review, may need additional images',
          stylistAction: 'Wait for completion or provide additional images',
          clientAction: 'May need to provide more photos',
          color: 'info',
          icon: 'refresh',
          priority: 9
        },
        authenticated_no_offer: {
          label: 'Authenticated - No Offer',
          description: 'Item passed authentication but no offer created yet',
          stylistAction: 'Follow up with admin to create offers',
          clientAction: 'Wait for offer',
          color: 'warning',
          icon: 'alert-circle',
          priority: 10
        },
        authenticated_with_offer: {
          label: 'Authenticated - Has Offer',
          description: 'Item authenticated and offers created',
          stylistAction: 'Present offers to client',
          clientAction: 'Review and respond to offers',
          color: 'success',
          icon: 'check-circle',
          priority: 11
        },
        offer_pending: {
          label: 'Offer Pending Response',
          description: 'Offers created, waiting for client decision',
          stylistAction: 'Follow up with client about offers',
          clientAction: 'Accept or reject offers',
          color: 'primary',
          icon: 'mail',
          priority: 12
        },
        offer_accepted: {
          label: 'Offer Accepted',
          description: 'Client accepted offer, ready for shipping',
          stylistAction: 'Arrange pickup or shipping',
          clientAction: 'Prepare item for pickup/shipping',
          color: 'success',
          icon: 'truck',
          priority: 13
        },
        in_transit: {
          label: 'In Transit',
          description: 'Item shipped, en route to warehouse',
          stylistAction: 'Track shipment status',
          clientAction: 'Wait for delivery confirmation',
          color: 'info',
          icon: 'package',
          priority: 14
        },
        received: {
          label: 'Received at Warehouse',
          description: 'Item arrived and checked in',
          stylistAction: 'Wait for product conversion',
          clientAction: 'Wait for product to go live',
          color: 'success',
          icon: 'check-square',
          priority: 15
        },
        converted: {
          label: 'Converted to Product',
          description: 'Item is now live on the site',
          stylistAction: 'Quote complete, notify client',
          clientAction: 'Item is live for sale',
          color: 'success',
          icon: 'star',
          priority: 16
        },
        fake: {
          label: 'Counterfeit Detected',
          description: 'Item failed authenticity checks',
          stylistAction: 'Inform client, close quote',
          clientAction: 'Quote cannot proceed',
          color: 'danger',
          icon: 'x-circle',
          priority: 7
        },
        needs_more_info: {
          label: 'Needs More Information',
          description: 'Additional information required to proceed',
          stylistAction: 'Work with client to provide info',
          clientAction: 'Provide additional information',
          color: 'warning',
          icon: 'help-circle',
          priority: 1
        },
        // Legacy statuses with priorities
        responded: {
          label: 'Responded',
          description: 'Client has responded to offers',
          stylistAction: 'Review client response',
          clientAction: 'Response submitted',
          color: 'info',
          icon: 'message-circle',
          priority: 12
        },
        offer_made: {
          label: 'Offer Made',
          description: 'Offers have been created and presented',
          stylistAction: 'Present offers to client',
          clientAction: 'Review offers',
          color: 'primary',
          icon: 'gift',
          priority: 13
        },
        pending_response: {
          label: 'Pending Response',
          description: 'Waiting for client response',
          stylistAction: 'Follow up with client',
          clientAction: 'Provide response',
          color: 'warning',
          icon: 'clock',
          priority: 14
        },
        rejected: {
          label: 'Rejected',
          description: 'Quote was rejected by client or admin',
          stylistAction: 'Close quote, follow up with client',
          clientAction: 'Quote rejected',
          color: 'danger',
          icon: 'x',
          priority: 15
        },
        shipped: {
          label: 'Shipped',
          description: 'Item has been shipped to warehouse',
          stylistAction: 'Track shipment',
          clientAction: 'Item shipped',
          color: 'info',
          icon: 'truck',
          priority: 16
        },
        approved: {
          label: 'Approved',
          description: 'Quote has been approved',
          stylistAction: 'Proceed with next steps',
          clientAction: 'Quote approved',
          color: 'success',
          icon: 'check',
          priority: 17
        }
      }
    }
  },
  methods: {
    async shareQuote () {
      try {
        await window.isClerkLoaded
        if (!window.Clerk.session) {
          this.shareMessage = 'Sorry, this service is unavailable. Try re-logging in.'
          return
        }

        this.isSharing = true
        this.shareMessage = ''

        await api.post('/api/stylist/share', {
          clientId: this.client?.id
        }).then(response => {
          if (response.data && response.data.message) {
            this.shareMessage = response.data.message
            this.$bvToast.toast(this.shareMessage, {
              title: 'Success',
              variant: 'success',
              solid: true
            })
          } else {
            this.shareMessage = 'Sent!'
          }
        })
      } catch (err) {
        this.shareMessage = 'Failed to send. Please try again.'
      } finally {
        this.isSharing = false
      }
    },

    loadBrandsFromCache () {
      const cachedDirectory = localStorage.getItem('designerDirectory')
      if (cachedDirectory) {
        const parsed = JSON.parse(cachedDirectory)
        if (Date.now() - parsed.timestamp < 86400000) {
          this.brands = parsed.brands
          return true
        }
      }
      return false
    },

    fetchAndCacheBrands () {
      return api
        .get('/api/v2/designer-directory')
        .then(response => {
          const brands = []
          const data = response.data.data
          Object.keys(data).forEach(id => {
            const brand = data[id]
            const categories = []
            Object.keys(brand.categories).forEach(categoryId => {
              categories.push({
                id: parseInt(categoryId),
                name: brand.categories[categoryId]
              })
            })
            brands.push({
              id: parseInt(id),
              name: brand.name,
              categories
            })
          })
          const sortedBrands = brands.sort((a, b) => a.name.localeCompare(b.name))
          this.brands = sortedBrands
          localStorage.setItem(
            'designerDirectory',
            JSON.stringify({ timestamp: Date.now(), brands: sortedBrands })
          )
        })
    },

    getDesignerDirectory () {
      if (!this.loadBrandsFromCache()) {
        this.fetchAndCacheBrands()
      }
    },
    getQuotes () {
      // Prevent duplicate API calls
      if (this.isFetchingQuotes) {
        return
      }

      const clientId = Number(this.$route.params.id)

      // Ensure client exists in store before trying to get quotes
      let client = this.$store.state.clients.find(c => c.id === clientId)
      if (!client) {
        const placeholderClient = {
          id: clientId,
          quotes: []
        }
        this.$store.dispatch('addClientToClients', placeholderClient)
        client = placeholderClient
      }

      // Check if quotes are already in Vuex store
      const existingQuotes = this.$store.getters.getClientQuotes(clientId)

      if (existingQuotes.length > 0) {
        // Show cached quotes immediately (no loading state)
        this.hasExistingQuotes = true
        this.loadingQuotes = false
      } else {
        // No cached quotes, show loading state
        this.loadingQuotes = true
      }

      // Always fetch fresh data from API and update store
      this.isFetchingQuotes = true
      this.$store.dispatch('getClientQuotes', clientId)
        .then(quotes => {
          this.hasExistingQuotes = quotes.length > 0
          this.loadingQuotes = false
          this.isFetchingQuotes = false
        })
        .catch(error => {
          console.error('Error loading quotes:', error)
          this.loadingQuotes = false
          this.isFetchingQuotes = false
        })
    },
    fetchClient () {
      api
        .get(
          '/api/stylist/clients/' +
          Number(this.$route.params.id)
        )
        .then(response => {
          const data = response.data.data
          const client = Object.assign({ id: parseInt(data.id) }, data.attributes)
          this.currentClient = client
          this.$store.dispatch('addClientToClients', client)
        })
    },
    finishedAddingItems () {
      this.isFinishedAddingItems = true
    },
    printShippingLabel () {
      // Check if we have accepted quotes selected
      if (this.includedItems.length > 0 && this.selectedQuoteStatus === 'Offer Accepted') {
        // TODO: Implement shipping label generation and printing for accepted quotes
        console.log('Generating shipping label for accepted quotes')
        // This could open a shipping label modal or redirect to shipping setup
        // For now, show a success message
        this.$bvToast.toast('Shipping label generated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } else {
        // Default behavior for other cases
        window.open(`/shipping-label/${this.$route.params.id}`, '_blank')
      }
    },

    async addedItem (newItem) {
      console.log('New item added:', newItem)

      // Show loading state immediately
      this.loadingQuotes = true

      // Add the new item to the store temporarily
      newItem.included = true
      this.$store.dispatch('addQuoteToClient', {
        clientId: Number(this.$route.params.id),
        quote: newItem
      })

      // Refresh quotes from API to get the latest data
      try {
        console.log('Refreshing quotes from API after adding new item...')
        await this.refreshQuotesFromAPI()
        console.log('Quotes refreshed successfully')

        // Show success toast
        this.$bvToast.toast('Quote added successfully and list refreshed!', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        console.error('Error refreshing quotes after adding item:', error)
        // Show error toast to user
        this.$bvToast.toast('Quote added but failed to refresh list. Please refresh the page.', {
          title: 'Warning',
          variant: 'warning',
          solid: true
        })
      } finally {
        // Ensure loading state is turned off
        this.loadingQuotes = false
      }
    },

    toggleIncluded (item) {
      if (!this.isFinishedAddingItems) {
        const itemStatus = this.getQuoteStatusText(item)

        // If no items are selected, allow selection
        if (this.includedItems.length === 0) {
          const newIncluded = !item.included
          this.$store.dispatch('updateQuoteIncluded', {
            clientId: Number(this.$route.params.id),
            quoteId: item.id,
            included: newIncluded
          })
          return
        }

        // If this item is already included, allow deselection
        if (item.included) {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId: Number(this.$route.params.id),
            quoteId: item.id,
            included: false
          })
          return
        }

        // If other items are selected, only allow selection if same status group
        const selectedStatus = this.getQuoteStatusText(this.includedItems[0])
        const itemStatusGroup = this.getStatusGroup(itemStatus)
        const selectedStatusGroup = this.getStatusGroup(selectedStatus)
        if (itemStatusGroup === selectedStatusGroup) {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId: Number(this.$route.params.id),
            quoteId: item.id,
            included: true
          })
        }
      }
    },

    selectAllFilteredItems () {
      if (!this.isFinishedAddingItems) {
        const clientId = Number(this.$route.params.id)

        // Get all currently displayed items (filtered by selected status)
        const itemsToSelect = this.displayedItems.filter(item => !item.included)

        if (itemsToSelect.length === 0) {
          return
        }

        // Select all filtered items
        itemsToSelect.forEach(item => {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId,
            quoteId: item.id,
            included: true
          })
        })
      }
    },

    deselectAllItems () {
      if (!this.isFinishedAddingItems) {
        const clientId = Number(this.$route.params.id)
        const itemsToDeselect = this.includedItems

        if (itemsToDeselect.length === 0) {
          return
        }

        // Deselect all included items
        itemsToDeselect.forEach(item => {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId,
            quoteId: item.id,
            included: false
          })
        })
      }
    },

    showPickupSignature () {
      this.$bvModal.hide('acceptedQuoteActions')
      this.signature = ''
      this.showSignaturePad = false
      this.signaturePadKey++

      this.$bvModal.show('pickupSignature')

      // Use nextTick to ensure modal is fully rendered before initializing signature pad
      this.$nextTick(() => {
        setTimeout(() => {
          this.showSignaturePad = true
        }, 100)
      })
    },

    onSignaturePadMounted () {
      console.log('Signature pad mounted successfully')
    },

    onSignatureModalHidden () {
      this.showSignaturePad = false
      this.signature = ''
    },

    getItemThumbnail (item) {
      // Check if item has a thumbnail image
      if (item.attributes && item.attributes.thumbnail_url) {
        return item.attributes.thumbnail_url
      }

      // Check if item has an image URL
      if (item.attributes && item.attributes.image_url) {
        return item.attributes.image_url
      }

      // Check if item has photos array
      if (item.attributes && item.attributes.images && item.attributes.images.length > 0) {
        return item.attributes.images[0].url
      }

      // Fallback to placeholder image
      return this.placeholderImgUrl
    },

    handleImageError (event) {
      // If image fails to load, use placeholder
      event.target.src = this.placeholderImgUrl
    },

    showReviewResponse (item) {
      this.acceptQuote = item
      this.$bvModal.hide('acceptedQuoteActions')
      this.initializeItemResponses()
      this.$bvModal.show('reviewResponse')
    },

    initializeItemResponses () {
      this.$set(this.itemResponses, this.acceptQuote.id, {})
    },

    getItemOffers (item) {
      console.log('Getting offers for item:', item.id, item.attributes)
      const offers = []

      // Check for different offer types - try multiple possible field names
      const consignmentFields = ['consignment_offer', 'consignmentOffer', 'consignment_amount', 'consignmentAmount']
      const buyoutFields = ['buyout_offer', 'buyoutOffer', 'buyout_amount', 'buyoutAmount']

      // Check consignment offers
      for (const field of consignmentFields) {
        if (item.attributes[field]) {
          offers.push({
            type: 'consignment',
            amount: item.attributes[field]
          })
          break
        }
      }

      // Check buyout offers
      for (const field of buyoutFields) {
        if (item.attributes[field]) {
          offers.push({
            type: 'buyout',
            amount: item.attributes[field]
          })
          break
        }
      }

      // Check if there's an offers object
      if (item.attributes.offers) {
        console.log('Found offers object:', item.attributes.offers)

        if (item.attributes.offers.consignment || item.attributes.offers.consignmentEstimate) {
          offers.push({
            type: 'consignment',
            amount: item.attributes.offers.consignment || item.attributes.offers.consignmentEstimate
          })
        }

        if (item.attributes.offers.buyout || item.attributes.offers.buyoutQuote) {
          offers.push({
            type: 'buyout',
            amount: item.attributes.offers.buyout || item.attributes.offers.buyoutQuote
          })
        }
      }

      console.log('Found offers for item', item.id, ':', offers)
      return offers
    },

    isOfferDisabled (item, offerType, action) {
      // Never disable buttons - let users freely choose accept/reject for each offer
      return false
    },

    formatOfferType (offerType) {
      const typeMap = {
        consignment: 'Consignment',
        buyout: 'Buyout'
      }
      return typeMap[offerType] || offerType
    },

    getOfferButtonClass (itemId, offerType, action) {
      const itemResponses = this.itemResponses[itemId] || {}
      const response = itemResponses[offerType]

      if (response === action) {
        // This action is selected for this offer
        return action === 'accept' ? 'btn-success' : 'btn-danger'
      } else if (response && response !== action) {
        // Different action is selected for this offer
        return 'btn-outline-secondary'
      } else {
        // No action selected yet for this offer
        return action === 'accept' ? 'btn-outline-success' : 'btn-outline-danger'
      }
    },

    onOfferSelectionChange (itemId, offerType, action) {
      if (!this.itemResponses[itemId]) {
        this.$set(this.itemResponses, itemId, {})
      }

      const itemResponses = this.itemResponses[itemId]

      // If clicking the same action on the same offer type, deselect it
      if (itemResponses[offerType] === action) {
        this.$delete(itemResponses, offerType)
        return
      }

      // If accepting an offer, reject all other offers for this item
      if (action === 'accept') {
        const currentItem = this.acceptQuote
        if (currentItem) {
          const offers = this.getItemOffers(currentItem)
          offers.forEach(offer => {
            if (offer.type === offerType) {
              // Accept this offer
              this.$set(itemResponses, offer.type, 'accept')
            } else {
              // Reject all other offers
              this.$set(itemResponses, offer.type, 'reject')
            }
          })
        }
      } else {
        // Just set reject for this specific offer
        this.$set(itemResponses, offerType, 'reject')
      }
    },

    onReviewResponseModalHidden () {
      this.itemResponses = {}
      this.submittingResponses = false
      this.acceptQuote = ''
    },

    async submitResponses () {
      if (!this.hasValidResponses) {
        this.$bvToast.toast('Please respond to all offers for each quote', {
          title: 'Response Required',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.submittingResponses = true

      try {
        const clientId = Number(this.$route.params.id)
        const payload = {
          data: Object.entries(this.itemResponses)
            .filter(([quoteId, offerResponses]) => {
              // Only include quotes that have at least one explicit response
              return Object.keys(offerResponses).length > 0 &&
                     Object.values(offerResponses).some(response => response === 'accept' || response === 'reject')
            })
            .map(([quoteId, offerResponses]) => {
              // Find the accepted offer type (if any)
              const acceptedOfferType = Object.entries(offerResponses).find(([offerType, response]) => response === 'accept')

              // Check if all offers are rejected
              const allRejected = Object.values(offerResponses).every(response => response === 'reject')

              return {
                id: quoteId,
                attributes: {
                  accepted_offer_type: allRejected ? 'rejected' : (acceptedOfferType ? acceptedOfferType[0] : null),
                  is_accepted: !allRejected && !!acceptedOfferType,
                  is_rejected: allRejected || !acceptedOfferType
                }
              }
            })
        }

        const response = await api.patch(`/api/stylist/clients/${clientId}/quotes`, payload)

        if (response.data) {
          this.$bvToast.toast('Responses submitted successfully!', {
            title: 'Success',
            variant: 'success',
            solid: true
          })

          // Clear selections and close modal
          this.itemResponses = {}
          this.$bvModal.hide('reviewResponse')

          // Reload quotes to get updated status
          this.reloadQuotes()
        }
      } catch (error) {
        console.error('Error submitting responses:', error)
        this.$bvToast.toast('Failed to submit responses. Please try again.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.submittingResponses = false
      }
    },

    confirmPickup () {
      if (!this.signature) {
        this.$bvToast.toast('Please provide a signature before confirming pickup', {
          title: 'Signature Required',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.loadingPickup = true
      // Pass the toggle value to markItemsAsPickedUp
      this.markItemsAsPickedUp(this.generateShippingLabel)
    },

    reviewQuotes () {
      const ids = this.includedItems.map(item => item.id).join(',')
      this.$router.push({
        name: 'quote-review',
        params: { id: this.$route.params.id },
        query: { quotes: ids }
      })
    },

    getQuoteStatusText (item) {
      const attrs = item.attributes

      // Check if item has been picked up (pickup_type is "concierge")
      if (attrs.pickup_type === 'concierge') {
        return 'Picked Up'
      }

      const status = attrs.status

      if (status) {
        // Use local statusMetadata for new statuses
        if (this.statusMetadata[status]) {
          return this.statusMetadata[status].label
        }

        // Legacy status mappings for backwards compatibility
        const legacyStatusMap = {
          Accepted: 'Accepted',
          Received: 'Received',
          Approved: 'Approved',
          Fake: 'Fake',
          Shipped: 'Shipped',
          Rejected: 'Rejected',
          'Needs More Info': 'Needs More Info',
          'Offer Made': 'Offer Made',
          Responded: 'Responded',
          'Pending Response': 'Pending Response',
          Pending: 'Pending'
        }

        // Check if it's a legacy status
        if (legacyStatusMap[status]) {
          return legacyStatusMap[status]
        }

        // Fallback: format camelCase with spaces
        return status.replace(/([a-z])([A-Z])/g, '$1 $2')
      }

      // Fallback for backwards compatibility
      return 'Not Submitted'
    },

    getQuoteStatusDescription (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].description
      }

      return 'Status information not available'
    },

    getStylistAction (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].stylistAction
      }

      return 'No action required'
    },

    getStatusPriority (item) {
      const attrs = item.attributes

      // Check if item has been picked up (pickup_type is "concierge")
      if (attrs.pickup_type === 'concierge') {
        return 5 // "Picked Up" priority
      }

      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].priority
      }

      // Legacy status mappings with priorities
      const legacyPriorityMap = {
        'Needs More Information': 1,
        Pending: 2,
        Responded: 3,
        Accepted: 4,
        'Picked Up': 5,
        Shipped: 6,
        Fake: 7,
        // Additional statuses with higher priorities
        'Pending Authentication': 8,
        'Authentication in Progress': 9,
        'Authenticated - No Offer': 10,
        'Authenticated - Has Offer': 11,
        'Offer Pending Response': 12,
        'Offer Accepted': 13,
        'In Transit': 14,
        'Received at Warehouse': 15,
        'Converted to Product': 16,
        'Counterfeit Detected': 7,
        'Offer Made': 17,
        'Pending Response': 18,
        Rejected: 19,
        Approved: 20,
        // Legacy status mappings for backwards compatibility
        Received: 15,
        'Needs More Info': 1
      }

      if (legacyPriorityMap[status]) {
        return legacyPriorityMap[status]
      }

      return 999 // Default high priority for unknown statuses
    },

    getStatusPriorityByText (statusText) {
      // Direct mapping for status text to priority
      const statusPriorityMap = {
        'Needs More Information': 1,
        'Needs More Info': 1,
        Pending: 2,
        Responded: 3,
        Accepted: 4,
        'Picked Up': 5,
        Shipped: 6,
        Fake: 7,
        'Counterfeit Detected': 7,
        // Additional statuses with higher priorities
        'Pending Authentication': 8,
        'Authentication in Progress': 9,
        'Authenticated - No Offer': 10,
        'Authenticated - Has Offer': 11,
        'Offer Pending Response': 12,
        'Offer Accepted': 13,
        'In Transit': 14,
        'Received at Warehouse': 15,
        'Converted to Product': 16,
        'Offer Made': 17,
        'Pending Response': 18,
        Rejected: 19,
        Approved: 20,
        // Legacy status mappings for backwards compatibility
        Received: 15
      }

      return statusPriorityMap[statusText] || 999
    },

    getStatusColor (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].color
      }

      return 'secondary'
    },

    getStatusLabel (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].label
      }

      return status || 'Unknown Status'
    },

    getStatusIcon (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Use local statusMetadata
      if (this.statusMetadata[status]) {
        return this.statusMetadata[status].icon
      }

      return 'help-circle'
    },

    hasOffers (item) {
      const attrs = item.attributes
      return attrs.offers && (
        attrs.offers.buybackQuote ||
        attrs.offers.buyoutQuote ||
        attrs.offers.consignmentEstimate
      )
    },

    getOfferAmount (item) {
      const attrs = item.attributes
      if (!attrs.offers) return null

      // Return the first available offer amount
      if (attrs.offers.buybackQuote) {
        return { type: 'Buyback', amount: attrs.offers.buybackQuote }
      }
      if (attrs.offers.buyoutQuote) {
        return { type: 'Buyout', amount: attrs.offers.buyoutQuote }
      }
      if (attrs.offers.consignmentEstimate) {
        return { type: 'Consignment', amount: attrs.offers.consignmentEstimate }
      }

      return null
    },

    getShipmentInfo (item) {
      const attrs = item.attributes
      if (!attrs.shipment) return null

      return {
        id: attrs.shipment.id,
        labelUrl: attrs.shipment.labelUrl,
        trackingNumber: attrs.shipment.trackingNumber,
        shiplistPdfLink: attrs.shipment.shiplistPdfLink
      }
    },

    validateQuoteData (item) {
      const attrs = item.attributes
      const issues = []

      // Check for required fields
      if (!attrs.status) {
        issues.push('Missing status information')
      }

      if (!attrs.name) {
        issues.push('Missing item name')
      }

      if (!attrs.brand) {
        issues.push('Missing brand information')
      }

      // Check for status-specific data requirements
      if (attrs.status === 'offer_pending' && !this.hasOffers(item)) {
        issues.push('Offer pending but no offers found')
      }

      if (attrs.status === 'in_transit' && !this.getShipmentInfo(item)) {
        issues.push('In transit but no shipment information')
      }

      return {
        isValid: issues.length === 0,
        issues: issues
      }
    },

    getStatusSummary () {
      if (this.includedItems.length === 0) {
        return 'No items selected'
      }

      const statusCounts = {}
      this.includedItems.forEach(item => {
        const status = this.getQuoteStatusText(item)
        statusCounts[status] = (statusCounts[status] || 0) + 1
      })

      const summary = Object.entries(statusCounts)
        .map(([status, count]) => `${count} ${status}`)
        .join(', ')

      return `${this.includedItems.length} item(s): ${summary}`
    },

    getNextSteps (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Define next steps based on status
      const nextStepsMap = {
        pending_authentication: 'Wait for admin review to complete',
        authentication_in_progress: 'Provide additional images if requested',
        authenticated_no_offer: 'Contact admin to request offer creation',
        authenticated_with_offer: 'Present offers to client for review',
        offer_pending: 'Follow up with client about pending offers',
        offer_accepted: 'Schedule pickup or arrange shipping',
        in_transit: 'Monitor shipment progress',
        received: 'Wait for warehouse processing',
        converted: 'Notify client that item is live',
        fake: 'Inform client and close quote',
        needs_more_info: 'Request specific information from client'
      }

      return nextStepsMap[status] || 'Review item status and take appropriate action'
    },

    getEstimatedTimeline (item) {
      const attrs = item.attributes
      const status = attrs.status

      // Define estimated timelines based on status
      const timelineMap = {
        pending_authentication: '1-3 business days',
        authentication_in_progress: '1-2 business days',
        authenticated_no_offer: '1-2 business days',
        authenticated_with_offer: 'Immediate',
        offer_pending: 'Client dependent',
        offer_accepted: '1-3 business days',
        in_transit: '3-7 business days',
        received: '2-5 business days',
        converted: 'Immediate',
        fake: 'Immediate',
        needs_more_info: 'Client dependent'
      }

      return timelineMap[status] || 'Varies by status'
    },

    signatureAdded (image) {
      this.signature = image
    },

    message (message, user) {
      return this.$helpers.dynamicContent(
        this.content['whiteGlove.' + message],
        user
      )
    },

    loadClient () {
      if (!this.client) {
        this.fetchClient()
      }
    },

    loadQuotes () {
      this.getQuotes()
    },

    loadBrands () {
      this.getDesignerDirectory()
    },

    clearSelectedItems () {
      const clientId = Number(this.$route.params.id)
      const quotes = this.$store.getters.getClientQuotes(clientId)
      quotes.forEach(item => {
        if (item.included) {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId,
            quoteId: item.id,
            included: false
          })
        }
      })
    },

    // Helper methods for status actions
    contactAdminForOffer () {
      // TODO: Implement contact admin functionality
      console.log('Contacting admin for offer creation')
      // This could open a modal or redirect to admin contact form
    },

    presentOffersToClient () {
      // TODO: Implement present offers functionality
      console.log('Presenting offers to client')
      // This could open a modal showing offer details or redirect to client communication
    },

    followUpWithClient () {
      // TODO: Implement client follow-up functionality
      console.log('Following up with client about offers')
      // This could open a communication modal or redirect to client messaging
    },

    arrangePickupOrShipping () {
      // Show the accepted quote actions modal
      this.$bvModal.show('acceptedQuoteActions')
    },

    trackShipment () {
      // TODO: Implement shipment tracking
      console.log('Tracking shipment status')
      // This could open tracking info or redirect to carrier tracking page
    },

    waitForConversion () {
      // TODO: Implement conversion waiting
      console.log('Waiting for product conversion')
      // This could show estimated timeline or status updates
    },

    notifyClientComplete () {
      // TODO: Implement client notification
      console.log('Notifying client of completion')
      // This could open a notification modal or send automated message
    },

    informClientFake () {
      // TODO: Implement fake item notification
      console.log('Informing client of counterfeit detection')
      // This could open a communication modal with template message
    },

    requestMoreInfo () {
      // TODO: Implement more info request
      console.log('Requesting more information from client')
      // This could open a request modal with specific questions
    },

    // Enhanced action methods that use helper data
    handleAuthenticatedNoOffer () {
      console.log('Handle Authenticated - No Offer status')
      // Check if we have client contact info and offer request template
      this.contactAdminForOffer()
    },

    handleAuthenticatedWithOffer () {
      console.log('Handle Authenticated - Has Offer status')
      // Check if offers exist and present them appropriately
      if (this.includedItems.length > 0) {
        const item = this.includedItems[0]
        if (this.hasOffers(item)) {
          const offer = this.getOfferAmount(item)
          console.log(`Presenting ${offer.type} offer: $${offer.amount}`)
        }
      }
      this.presentOffersToClient()
    },

    handleOfferAccepted () {
      console.log('Handle Offer Accepted status')
      // Check if we have pickup/shipping preferences
      this.arrangePickupOrShipping()
    },

    handleInTransit () {
      console.log('Handle In Transit status')
      // Check if we have tracking info and show it
      if (this.includedItems.length > 0) {
        const item = this.includedItems[0]
        const shipment = this.getShipmentInfo(item)
        if (shipment && shipment.trackingNumber) {
          console.log(`Tracking shipment: ${shipment.trackingNumber}`)
        }
      }
      this.trackShipment()
    },

    handleReceived () {
      console.log('Handle Received at Warehouse status')
      // Show estimated conversion timeline
      this.waitForConversion()
    },

    handleConverted () {
      console.log('Handle Converted to Product status')
      // Check if we have client notification preferences
      this.notifyClientComplete()
    },

    handleFake () {
      console.log('Handle Counterfeit Detected status')
      // Check if we have client communication history
      this.informClientFake()
    },

    handleNeedsMoreInfo () {
      console.log('Handle Needs More Information status')
      // Check what specific info is needed
      this.requestMoreInfo()
    },

    // Additional status handlers for legacy compatibility
    handlePendingAuthentication () {
      console.log('Handle Pending Authentication status')
      // No action needed - wait for admin review
    },

    handleAuthenticationInProgress () {
      console.log('Handle Authentication in Progress status')
      // No action needed - wait for completion
    },

    handleApproved () {
      console.log('Handle Approved status (legacy)')
      // Map to authenticated with offer
      this.handleAuthenticatedWithOffer()
    },

    handleShipped () {
      console.log('Handle Shipped status (legacy)')
      // Map to in transit
      this.handleInTransit()
    },

    handleAccepted () {
      console.log('Handle Accepted status (legacy)')
      // Map to offer accepted
      this.handleOfferAccepted()
    },

    handleRejected () {
      console.log('Handle Rejected status (legacy)')
      // Map to fake/counterfeit
      this.handleFake()
    },

    handleOfferMade () {
      console.log('Handle Offer Made status (legacy)')
      // Map to authenticated with offer
      this.handleAuthenticatedWithOffer()
    },

    handleResponded () {
      console.log('Handle Responded status (legacy)')
      // Map to authenticated with offer
      this.handleAuthenticatedWithOffer()
    },

    getStatusActionMethod (status) {
      const statusMethodMap = {
        'Pending Authentication': this.handlePendingAuthentication,
        'Authentication in Progress': this.handleAuthenticationInProgress,
        'Authenticated - No Offer': this.handleAuthenticatedNoOffer,
        'Authenticated - Has Offer': this.handleAuthenticatedWithOffer,
        'Offer Accepted': this.handleOfferAccepted,
        'In Transit': this.handleInTransit,
        'Received at Warehouse': this.handleReceived,
        'Converted to Product': this.handleConverted,
        'Counterfeit Detected': this.handleFake,
        'Needs More Information': this.handleNeedsMoreInfo,
        // Legacy status mappings for backwards compatibility
        Received: this.handleReceived,
        Approved: this.handleAuthenticatedWithOffer,
        Fake: this.handleFake,
        Shipped: this.handleInTransit,
        Accepted: this.handleOfferAccepted,
        Rejected: this.handleFake,
        'Needs More Info': this.handleNeedsMoreInfo,
        'Offer Made': this.handleAuthenticatedWithOffer,
        Responded: this.showReviewResponse
      }
      return statusMethodMap[status] || (() => console.log('Unknown status:', status))
    },

    getStatusGroup (status) {
      // Group statuses that should be selectable together based on new API statuses
      const statusGroups = {
        // Authentication group - items being reviewed
        'Pending Authentication': 'Authentication',
        'Authentication in Progress': 'Authentication',

        // Offer management group - items ready for client interaction
        'Authenticated - No Offer': 'OfferManagement',
        'Authenticated - Has Offer': 'OfferManagement',
        'Offer Pending Response': 'OfferManagement',

        // Processing group - items being handled
        'Offer Accepted': 'Processing',
        'In Transit': 'Processing',
        'Received at Warehouse': 'Processing',

        // Completion group - items finished
        'Converted to Product': 'Completion',

        // Issue group - items with problems
        'Counterfeit Detected': 'Issue',
        'Needs More Information': 'Issue'
      }

      // Legacy status groupings for backwards compatibility
      const legacyGroups = {
        Fake: 'Issue',
        'Needs More Info': 'Issue',
        'Offer Made': 'OfferManagement',
        Responded: 'OfferManagement',
        Accepted: 'Processing'
      }

      return statusGroups[status] || legacyGroups[status] || status
    },

    isItemClickable (item) {
      // If no items are selected, all items are clickable
      if (this.includedItems.length === 0) {
        return true
      }

      // If this item is already included, it's clickable (for deselection)
      if (item.included) {
        return true
      }

      // If other items are selected, only items with same status group are clickable
      const itemStatus = this.getQuoteStatusText(item)
      const itemStatusGroup = this.getStatusGroup(itemStatus)
      const selectedStatusGroup = this.getStatusGroup(this.selectedQuoteStatus)
      return itemStatusGroup === selectedStatusGroup
    },
    reloadQuotes () {
      this.loadingQuotes = true
      const clientId = Number(this.$route.params.id)
      console.log('Reloading quotes for client:', clientId)

      // Force fresh fetch from API by calling Vuex action directly
      this.$store.dispatch('getClientQuotes', clientId)
        .then(quotes => {
          console.log('Quotes reloaded from API:', quotes)
          this.hasExistingQuotes = quotes.length > 0
          this.loadingQuotes = false
        })
        .catch(error => {
          console.error('Error reloading quotes:', error)
          this.loadingQuotes = false
        })
    },

    // Force refresh quotes and apply sorting
    forceRefreshQuotes () {
      console.log('Force refreshing quotes and applying sorting...')
      this.loadingQuotes = true

      // Clear the current quotes to force a fresh load
      this.$store.dispatch('clearClientQuotes', Number(this.$route.params.id))

      // Reload quotes
      this.getQuotes()

      // Force a reactive update
      this.$forceUpdate()
    },

    // Refresh quotes from API and update store
    async refreshQuotesFromAPI () {
      console.log('Refreshing quotes from API...')
      this.loadingQuotes = true

      try {
        const clientId = Number(this.$route.params.id)

        // Fetch fresh quotes from API
        const response = await api.get(`/api/stylist/clients/${clientId}/quotes`)
        console.log('Fresh quotes API response:', response)

        const data = Array.isArray(response.data) ? response.data : response.data.data || []
        console.log('Fresh quotes data:', data)

        // Transform the data to include the included property
        const quotes = data.map(item => ({
          id: item.id,
          included: false,
          attributes: item
        }))

        // Update the store with fresh quotes
        this.$store.dispatch('getClientQuotes', clientId)

        // Update hasExistingQuotes flag
        this.hasExistingQuotes = quotes.length > 0

        console.log('Quotes refreshed from API, count:', quotes.length)

        // Force Vue to recalculate computed properties
        this.$nextTick(() => {
          console.log('displayedItems after refresh:', this.displayedItems.length)
          console.log('First few items after refresh:')
          this.displayedItems.slice(0, 5).forEach((item, index) => {
            const statusText = this.statusMap[item.id]
            const priority = this.getStatusPriorityByText(statusText)
            console.log(`${index + 1}. [Priority: ${priority}] "${statusText}" - ID: ${item.id}`)
          })
        })
      } catch (error) {
        console.error('Error refreshing quotes from API:', error)
        throw error
      } finally {
        this.loadingQuotes = false
      }
    },

    handleShippingLabel () {
      this.$bvModal.hide('acceptedQuoteActions')
      // Clear any previous errors when opening the modal
      this.clearShippingLabelErrors()
      this.$bvModal.show('shippingLabelOptions')
    },

    clearShippingLabelErrors () {
      this.shippingLabelErrors = []
    },

    cancelShippingLabelModal () {
      this.clearShippingLabelErrors()
      this.$bvModal.hide('shippingLabelOptions')
    },

    handlePickup () {
      console.log('Pickup button clicked')
      console.log('Included items:', this.includedItems)
      console.log('Included items length:', this.includedItems.length)

      // Don't hide the modal immediately - wait for the API call to complete
      this.markItemsAsPickedUp()
    },

    async markItemsAsPickedUp (generateShippingLabel = false) {
      console.log('markItemsAsPickedUp method called')
      console.log('Included items in markItemsAsPickedUp:', this.includedItems)
      console.log('Included items length in markItemsAsPickedUp:', this.includedItems.length)
      console.log('generateShippingLabel:', generateShippingLabel)

      if (!this.includedItems.length) {
        console.log('No items selected - showing error toast')
        this.$bvToast.toast('No items selected for pickup', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        return
      }

      this.loadingPickup = true
      console.log('Loading pickup set to true')

      try {
        const clientId = Number(this.$route.params.id)
        this.isSubmitting = true
        const includedItemIds = this.includedItems
          .map(item => item.id)
          .join(',')
        api
          .post(
            '/api/stylist/white-glove-quotes/complete',
            {
              quoteIds: includedItemIds,
              signature: this.signature,
              generateShippingLabel: generateShippingLabel
            },
            {
              headers: {
                'Content-Type': 'application/x.fashionphile.v1+json, application/json'
              }
            }
          )
          .then(response => {
            this.isCompleted = true
            if (response.data) {
              // Success - show success message
              this.$bvToast.toast('Items marked as picked up successfully!', {
                title: 'Success',
                variant: 'success',
                solid: true
              })

              // Clear selected items after successful pickup marking
              this.includedItems.forEach(item => {
                this.$store.dispatch('updateQuoteIncluded', {
                  clientId,
                  quoteId: item.id,
                  included: false
                })
              })

              this.isSubmitting = false
              this.reloadQuotes()

              // Close the signature modal
              this.$bvModal.hide('pickupSignature')

              // Reset to allow adding more items
              this.isFinishedAddingItems = false
            }
          })
          .catch(error => {
            this.isSubmitting = false
            this.errorMessages = error.response.data.errors
          })
      } catch (error) {
        console.error('Error scheduling pickup:', error)

        let errorMessage = 'Failed to mark items as picked up'

        if (error.response && error.response.data) {
          const errorData = error.response.data
          if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.error) {
            errorMessage = errorData.error
          }
        }

        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loadingPickup = false
      }
    },

    // Debug method for testing
    debugPickupState () {
      console.log('=== Debug Pickup State ===')
      console.log('isFinishedAddingItems:', this.isFinishedAddingItems)
      console.log('includedItems:', this.includedItems)
      console.log('includedItems.length:', this.includedItems.length)
      console.log('pendingItems:', this.pendingItems)
      console.log('pendingItems with included:', this.pendingItems.map(item => ({
        id: item.id,
        included: item.included,
        status: item.attributes?.status
      })))
      console.log('Client ID:', this.$route.params.id)
      console.log('=======================')
    },

    // Debug method for status sorting
    debugStatusSorting () {
      console.log('=== Debug Status Sorting ===')

      const items = this.pendingItems
      console.log('Total items:', items.length)

      // Show each item with its status and priority
      items.forEach((item, index) => {
        const statusText = this.statusMap[item.id]
        const priority = this.getStatusPriorityByText(statusText)
        console.log(`${index + 1}. ID: ${item.id}, Status: "${statusText}", Priority: ${priority}`)
      })

      // Show the sorted order
      console.log('\n=== Sorted Order ===')
      const sortedItems = [...items].sort((a, b) => {
        const aStatusText = this.statusMap[a.id]
        const bStatusText = this.statusMap[b.id]
        const aPriority = this.getStatusPriorityByText(aStatusText)
        const bPriority = this.getStatusPriorityByText(bStatusText)

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }
        return new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
      })

      sortedItems.forEach((item, index) => {
        const statusText = this.statusMap[item.id]
        const priority = this.getStatusPriorityByText(statusText)
        console.log(`${index + 1}. [Priority: ${priority}] "${statusText}" - ID: ${item.id}`)
      })

      console.log('=== End Debug ===')
    },

    // Debug method for specific item (like the Fake status item)
    debugSpecificItem (itemId) {
      console.log(`=== Debug Item ${itemId} ===`)

      const item = this.pendingItems.find(i => i.id === itemId)
      if (!item) {
        console.log(`Item ${itemId} not found in pendingItems`)
        return
      }

      console.log('Raw item:', item)
      console.log('Raw status attribute:', item.attributes.status)
      console.log('Status text from getQuoteStatusText:', this.getQuoteStatusText(item))
      console.log('Status text from statusMap:', this.statusMap[item.id])
      console.log('Priority from getStatusPriorityByText:', this.getStatusPriorityByText(this.statusMap[item.id]))
      console.log('Priority from getStatusPriority:', this.getStatusPriority(item))

      // Check if this item is in the displayedItems
      const displayedIndex = this.displayedItems.findIndex(i => i.id === itemId)
      console.log('Index in displayedItems:', displayedIndex)

      if (displayedIndex !== -1) {
        console.log('Items around this one in displayedItems:')
        const start = Math.max(0, displayedIndex - 2)
        const end = Math.min(this.displayedItems.length, displayedIndex + 3)

        for (let i = start; i < end; i++) {
          const displayItem = this.displayedItems[i]
          const statusText = this.statusMap[displayItem.id]
          const priority = this.getStatusPriorityByText(statusText)
          console.log(`${i + 1}. [Priority: ${priority}] "${statusText}" - ID: ${displayItem.id}`)
        }
      }

      console.log('=== End Debug ===')
    },

    // Test method to manually set items as included
    testIncludeItems () {
      console.log('Testing include items...')
      if (this.pendingItems.length > 0) {
        // Set first item as included
        this.$store.dispatch('updateQuoteIncluded', {
          clientId: Number(this.$route.params.id),
          quoteId: this.pendingItems[0].id,
          included: true
        })
        console.log('Set first item as included')
      }
    },

    // Test method to enter finished adding items mode
    testEnterFinishedMode () {
      console.log('Entering finished adding items mode...')
      this.isFinishedAddingItems = true
      console.log('isFinishedAddingItems set to:', this.isFinishedAddingItems)
    },

    async createShippingLabels () {
      if (!this.includedItems.length) {
        this.$bvToast.toast('No items selected for shipping labels', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        return
      }

      const clientId = Number(this.$route.params.id)
      const itemIds = this.includedItems.map(item => item.id)

      const requestData = {
        item_ids: itemIds,
        emailClientShiplist: this.emailClientShiplist
      }

      // Clear previous errors
      this.shippingLabelErrors = []

      // Validate request data using the service
      const validation = shippingLabelService.validateLabelData(requestData)
      if (!validation.isValid) {
        // Map validation errors to field errors
        validation.errors.forEach(error => {
          if (error.includes('Item IDs')) {
            this.shippingLabelErrors.push(error)
          } else {
            this.shippingLabelErrors.push(error)
          }
        })
        return
      }

      this.isCreatingLabels = true

      try {
        console.log('Creating shipping labels with data:', requestData)

        const response = await shippingLabelService.createShippingLabels(clientId, requestData)
        if (response.data) {
          // Success - show results
          this.$bvModal.hide('shippingLabelOptions')

          // Clear any errors on success
          this.clearShippingLabelErrors()

          this.createdLabel = response.data
          const trackingNumber = this.createdLabel.tracking_number

          this.$bvToast.toast(`Shipping label created successfully! Tracking: ${trackingNumber}`, {
            title: 'Success',
            variant: 'success',
            solid: true,
            noAutoHide: true
          })

          // Clear selected items
          this.includedItems.forEach(item => {
            this.$store.dispatch('updateQuoteIncluded', {
              clientId,
              quoteId: item.id,
              included: false
            })
          })

          // Reload quotes to get updated status
          this.reloadQuotes()
        }
      } catch (error) {
        console.error('Error creating shipping labels:', error)

        // Clear previous errors
        this.shippingLabelErrors = []

        if (error.response && error.response.data) {
          const errorData = error.response.data

          if (errorData.errors) {
            // Handle field-level validation errors
            Object.keys(errorData.errors).forEach(field => {
              const fieldErrors = errorData.errors[field]
              if (Array.isArray(fieldErrors)) {
                const errorMessage = fieldErrors.join(', ')

                if (field === 'item_ids' || field.includes('item_ids')) {
                  this.shippingLabelErrors.push(`Items: ${errorMessage}`)
                } else {
                  this.shippingLabelErrors.push(`${field}: ${errorMessage}`)
                }
              }
            })
          } else if (errorData.message) {
            this.shippingLabelErrors.push(errorData.message)
          } else if (errorData.error) {
            this.shippingLabelErrors.push(errorData.error)
          }
        }

        // If no specific errors were parsed, add a generic error
        if (this.shippingLabelErrors.length === 0) {
          this.shippingLabelErrors.push('Failed to create shipping labels. Please try again.')
        }

        // Still show a toast for immediate feedback
        this.$bvToast.toast('Error creating shipping labels. See details above.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isCreatingLabels = false
      }
    }
  },
  watch: {
    selectedStatus (newStatus) {
      if (newStatus === 'All') {
        return
      }
      const clientId = Number(this.$route.params.id)
      this.pendingItems.forEach(item => {
        const status = this.statusMap[item.id]
        if (status !== newStatus && item.included) {
          this.$store.dispatch('updateQuoteIncluded', {
            clientId,
            quoteId: item.id,
            included: false
          })
        }
      })
    },
    pendingItems: {
      handler (newItems, oldItems) {
        // Watch will trigger re-calculation of displayedItems automatically
      },
      deep: true
    }
  },
  mounted () {
    document.body.setAttribute('data-layout', 'lightHeader')
    this.loadClient()
    this.loadQuotes()
    this.loadBrands()

    // Make debug methods available in browser console
    if (typeof window !== 'undefined') {
      window.debugPickupState = this.debugPickupState
      window.testIncludeItems = this.testIncludeItems
      window.testEnterFinishedMode = this.testEnterFinishedMode
      window.debugStatusSorting = this.debugStatusSorting
      window.debugSpecificItem = this.debugSpecificItem
      window.forceRefreshQuotes = this.forceRefreshQuotes
      window.refreshQuotesFromAPI = this.refreshQuotesFromAPI
      window.pickupComponent = this
    }
  },
  computed: {
    pendingItems () {
      const items = this.$store.getters.getClientQuotes(Number(this.$route.params.id))
      console.log('[PICKUP] pendingItems computed - returning', items.length, 'items')
      return items
    },
    includedItems () {
      return this.pendingItems.filter(
        pendingItem => pendingItem.included === true
      )
    },
    statusMap () {
      const map = {}
      this.pendingItems.forEach(item => {
        map[item.id] = this.getQuoteStatusText(item)
      })
      return map
    },
    displayedItems () {
      const initialItems = this.isFinishedAddingItems ? this.includedItems : this.pendingItems
      console.log('[PICKUP] displayedItems computed - initialItems:', initialItems.length)
      let items = initialItems
      if (this.selectedStatus !== 'All') {
        items = items.filter(item => {
          const status = this.statusMap[item.id]
          return status === this.selectedStatus
        })
      }

      // Sort items by numeric priority and then by creation date
      items.sort((a, b) => {
        // Get the display text for each item
        const aStatusText = this.statusMap[a.id]
        const bStatusText = this.statusMap[b.id]

        // Get priority based on display text
        const aPriority = this.getStatusPriorityByText(aStatusText)
        const bPriority = this.getStatusPriorityByText(bStatusText)

        // Debug logging for sorting
        if (a.id === 1 || b.id === 1 || a.id === 4474118 || b.id === 4474118) { // Log for specific items to avoid spam
          console.log(`Sorting: Item ${a.id} ("${aStatusText}" priority ${aPriority}) vs Item ${b.id} ("${bStatusText}" priority ${bPriority})`)
        }

        if (aPriority !== bPriority) {
          return aPriority - bPriority
        }

        // Secondary sort by creation date (newest first)
        return new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt)
      })

      console.log('[PICKUP] displayedItems computed - returning', items.length, 'items')
      return items
    },
    statusCounts () {
      const counts = {}
      this.pendingItems.forEach(item => {
        const status = this.statusMap[item.id]
        counts[status] = (counts[status] || 0) + 1
      })
      return counts
    },
    filterOptions () {
      const options = [
        { value: 'All', label: 'All', count: this.pendingItems.length }
      ]

      // Create status options with priority for sorting
      const statusOptions = Object.keys(this.statusCounts).map(status => ({
        value: status,
        label: status,
        count: this.statusCounts[status],
        priority: this.getStatusPriorityByText(status)
      }))

      // Sort by priority (lowest number first)
      statusOptions.sort((a, b) => a.priority - b.priority)

      // Add sorted status options
      statusOptions.forEach(option => {
        options.push(option)
      })

      return options
    },
    hasValidResponses () {
      const offers = this.getItemOffers(this.acceptQuote)
      if (offers.length === 0) return false

      const itemResponses = this.itemResponses[this.acceptQuote.id] || {}
      return offers.some(offer => {
        return itemResponses[offer.type] === 'accept' || itemResponses[offer.type] === 'reject'
      })
    },
    hasRespondedQuotes () {
      const responded = ['responded', 'offer made', 'needs more info', 'needs info', 'fake']
      return this.includedItems.some(item => {
        const status = this.statusMap[item.id]
        return responded.includes(status.toLowerCase())
      })
    },
    content () {
      return this.$store.state.il8nContent
    },
    client () {
      if (this.$route.params.id) {
        const found = this.$store.state.clients.find(
          client => client.id === Number(this.$route.params.id)
        )
        if (found) {
          return found
        }
        return this.currentClient
      }
      return ''
    },
    selectedQuoteStatus () {
      if (this.includedItems.length > 0) {
        return this.getQuoteStatusText(this.includedItems[0])
      }
      return null
    },
    availableItems () {
      if (this.includedItems.length === 0) {
        return this.pendingItems
      }
      const selectedStatus = this.selectedQuoteStatus
      const selectedStatusGroup = this.getStatusGroup(selectedStatus)
      return this.pendingItems.filter(item => {
        const itemStatus = this.getQuoteStatusText(item)
        const itemStatusGroup = this.getStatusGroup(itemStatus)
        return itemStatusGroup === selectedStatusGroup || item.included
      })
    },
    statusButtonTextMap () {
      return {
        // New API statuses
        'Pending Authentication': 'Wait for Review',
        'Authentication in Progress': 'Wait for Completion',
        'Authenticated - No Offer': 'Contact Admin for Offer',
        'Authenticated - Has Offer': 'Present Offers to Client',
        'Offer Pending Response': 'Follow Up with Client',
        'Offer Accepted': 'Pickup/Shipping Label',
        'In Transit': 'Track Shipment',
        'Received at Warehouse': 'Wait for Conversion',
        'Converted to Product': 'Quote Complete',
        'Counterfeit Detected': 'Inform Client',
        'Needs More Information': 'Request More Info',

        // Legacy status mappings for backwards compatibility
        Received: 'Mark as Received',
        Approved: 'Approve Quotes',
        Fake: 'Review Response',
        Shipped: 'Mark as Shipped',
        Accepted: 'Pickup/Shipping Label',
        Responded: 'Review Response',
        Rejected: 'Reject Quotes',
        'Needs More Info': 'Review Response',
        'Offer Made': 'Review Response',
        'Pending Response': 'Mark Pending'
      }
    },
    selectedStatusButtonText () {
      if (this.selectedQuoteStatus) {
        return this.statusButtonTextMap[this.selectedQuoteStatus] || this.selectedQuoteStatus
      }
      return null
    },

    isStatusActionable () {
      if (!this.selectedQuoteStatus) return false

      return this.actionableStatuses.includes(this.selectedQuoteStatus)
    },

    shouldShowStatusButton () {
      return this.selectedQuoteStatus &&
             this.includedItems.length > 0 &&
             this.isStatusActionable &&
             !this.isFinishedAddingItems
    }
  }
}

</script>
<style>
.confirmation-modal{
background-color:#eeefee !important;
}
.confirmation-modal .modal-footer{
  border-color: #ccc;
}
.alert-danger a:link, .alert-danger a:visited {
  color: red !important;
  padding-left: 10px;
}
.placeholder-img {
  width: 60px;
  height: 60px;
  display: inline-block;
  background-color: #f0f0f0;
}

.loading-card {
  animation: pulseBg 1.5s ease-in-out infinite;
}

.pulse {
  animation: pulseBg 1.5s ease-in-out infinite;
}

@keyframes pulseBg {
  0% {
    background-color: #f0f0f0;
  }
  50% {
    background-color: #e0e0e0;
  }
  100% {
    background-color: #f0f0f0;
  }
}

/* Quote grid layout */
.quote-grid {
  display: grid !important;
  gap: 1rem;
  grid-template-columns: 1fr;
  width: 100%;
}

/* Responsive grid - 2 columns for tablets */
@media (min-width: 768px) {
  .quote-grid {
    grid-template-columns: repeat(2, 1fr) !important;
  }
}

/* 3 columns for desktop */
@media (min-width: 1024px) {
  .quote-grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }
}

.filter-dropdown select,
.filter-dropdown select:focus {
  background-color: #fff;
  color: #212529 !important;
  border: 1px solid #343a40;
  font-weight: 700;
}

.selected-quotes-list {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
}

.quote-item {
  background-color: #fff;
  border: 1px solid #dee2e6 !important;
}

.quote-item:last-child {
  margin-bottom: 0 !important;
}

/* Item thumbnail styles */
.item-thumbnail {
  flex-shrink: 0;
}

.thumbnail-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
}

.item-details {
  min-width: 0;
}

/* Accepted Quote Actions Modal Styles */
#acceptedQuoteActions .modal-content {
  background-color: #f8f9fa !important;
  max-height: 90vh;
}

#acceptedQuoteActions .modal-dialog {
  max-width: 95vw;
  max-height: 90vh;
  margin: 1rem auto;
}

#acceptedQuoteActions .modal-body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

#acceptedQuoteActions .modal-header {
  padding: 1rem 1.5rem;
}

#acceptedQuoteActions .modal-footer {
  padding: 1rem 1.5rem;
}

.accepted-quote-actions-content {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.accepted-quote-actions-content > p {
  flex-shrink: 0;
}

.accepted-quote-actions-content .selected-quotes-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  margin-bottom: 1rem;
}

.accepted-quote-actions-content .quote-item {
  background-color: #fff;
  border: 1px solid #dee2e6 !important;
}

.accepted-quote-actions-content .quote-item:last-child {
  margin-bottom: 0 !important;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

/* Shipping Label Options Modal Styles */
#shippingLabelOptions .modal-content {
  background-color: #f8f9fa !important;
}

.shipping-label-options-content {
  max-height: 600px;
  overflow-y: auto;
}

.shipping-label-options-content .selected-quotes-summary {
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
}

.shipping-label-options-content .selected-quotes-list {
  max-height: 200px;
  overflow-y: auto;
}

.shipping-label-options-content .quote-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6 !important;
}

.shipping-label-options-content .quote-item:last-child {
  margin-bottom: 0 !important;
}

.shipping-label-options-content .form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.shipping-label-options-content .form-control,
.shipping-label-options-content .shipping-select {
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 0.5rem 0.75rem;
  font-size: 14px;
  background-color: #fff !important;
  color: #495057 !important;
}

.shipping-label-options-content .form-control:focus,
.shipping-label-options-content .shipping-select:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  background-color: #fff !important;
  color: #495057 !important;
}

.shipping-label-options-content .shipping-select option {
  background-color: #fff !important;
  color: #495057 !important;
}

.shipping-label-options-content .shipping-select optgroup {
  background-color: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600;
}

.shipping-label-options-content .form-actions {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.shipping-label-options-content .form-text {
  font-size: 12px;
  margin-top: 0.25rem;
}

.shipping-label-options-content .form-text.text-muted {
  color: #6c757d !important;
}

.shipping-label-options-content .form-text.text-danger {
  color: #dc3545 !important;
}

.shipping-label-options-content .form-text i {
  margin-right: 0.25rem;
}

/* Error Display Styles */
.shipping-label-options-content .error-alert {
  border-radius: 8px;
  border: 1px solid #dc3545;
}

.shipping-label-options-content .error-alert .alert {
  margin-bottom: 0;
  border: none;
  background-color: #f8d7da;
}

.shipping-label-options-content .error-alert ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.shipping-label-options-content .error-alert li {
  color: #721c24;
  font-size: 14px;
  line-height: 1.4;
}

.shipping-label-options-content .field-errors-summary strong {
  color: #721c24;
  font-size: 13px;
}

.shipping-label-options-content .field-errors-summary ul {
  padding-left: 20px;
  margin-bottom: 0;
  margin-top: 8px;
}

.shipping-label-options-content .form-control.is-invalid {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.shipping-label-options-content .invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 13px;
  margin-top: 4px;
  font-weight: 500;
}

/* Error Badge Styles */
.shipping-label-options-content .badge-danger {
  background-color: #dc3545;
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
}

/* Modal Header Styles */
.shipping-label-options-content .modal-header {
  border-bottom: 2px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.shipping-label-options-content .close-modal {
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.shipping-label-options-content .close-modal:hover {
  background-color: #f8f9fa;
}

.shipping-label-options-content .close-modal i {
  font-size: 18px;
  color: #6c757d;
}

/* Mobile Action Bar Styles */
.mobile-action-bar {
  position: fixed;
  bottom: 80px; /* Position above the navbar */
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 2px solid #e9ecef;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 999;
  padding: 12px 16px;
}

.action-bar-content {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-direction: column;
}

.mobile-selected-count {
  width: 100%;
  text-align: center;
  margin-bottom: 8px;
}

.mobile-selected-count span {
  font-size: 12px;
  font-weight: bold;
  color: #333;
}

.mobile-action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Ensure content doesn't get hidden behind action bar */
@media (max-width: 767px) {
  .container.white-glove {
    padding-bottom: 160px; /* Add space for both action bar and navbar */
  }
}

/* Pickup Signature Modal Styles */
#pickupSignature .modal-content {
  background-color: #f8f9fa !important;
  max-height: 90vh;
}

#pickupSignature .modal-dialog {
  max-width: 95vw;
  max-height: 90vh;
  margin: 1rem auto;
}

#pickupSignature .modal-body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

#pickupSignature .modal-header {
  padding: 1rem 1.5rem;
}

#pickupSignature .modal-footer {
  padding: 1rem 1.5rem;
}

/* Ensure the signature pad container uses the full available height */
.signature-pad-container {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
  min-height: 350px;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  #pickupSignature .modal-dialog,
  #acceptedQuoteActions .modal-dialog,
  #reviewResponse .modal-dialog {
    max-width: 98vw;
    margin: 0.5rem auto;
  }

  #pickupSignature .modal-body,
  #acceptedQuoteActions .modal-body,
  #reviewResponse .modal-body {
    max-height: calc(90vh - 120px);
  }

  .signature-pad-container {
    min-height: 250px;
  }
}

.pickup-signature-content {
  height: 100%;
  overflow-y: auto;
}

.signature-pad-container {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}

.signature-pad-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 2rem;
}

.signature-actions {
  display: flex;
  justify-content: center;
}

.pickup-confirmation-text {
  background-color: #fff;
  border-radius: 0.25rem;
}

.pickup-confirmation-text .alert {
  margin-bottom: 0;
}

.selected-quotes-summary {
  background-color: #fff;
  border-radius: 0.25rem;
  padding: 1rem;
  border: 1px solid #dee2e6;
}

/* Review Response Modal Styles */
#reviewResponse .modal-content {
  background-color: #f8f9fa !important;
  max-height: 90vh;
}

#reviewResponse .modal-dialog {
  max-width: 95vw;
  max-height: 90vh;
  margin: 1rem auto;
}

#reviewResponse .modal-body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
}

#reviewResponse .modal-header {
  padding: 1rem 1.5rem;
}

#reviewResponse .modal-footer {
  padding: 1rem 1.5rem;
}

.review-response-content {
  height: 100%;
  overflow-y: auto;
}

.quote-offer-item {
  background-color: #fff;
}

.offers-section {
  margin-top: 1rem;
}

.offers-list {
  max-height: 300px;
  overflow-y: auto;
}

.offer-item {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6 !important;
  transition: all 0.2s ease;
}

.offer-item:hover {
  background-color: #e9ecef;
}

.offer-item.offer-disabled {
  background-color: #f1f3f4;
  opacity: 0.6;
}

.offer-item.offer-disabled .offer-info {
  color: #6c757d;
}

.offer-layout {
  width: 100%;
}

.offer-info {
  flex: 1;
  min-width: 0;
}

.offer-amount {
  flex-shrink: 0;
  font-size: 1.2rem;
  text-align: right;
  min-width: 80px;
}

.offer-actions {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.form-check-input:disabled + .form-check-label {
  color: #6c757d;
  cursor: not-allowed;
}
.btn-primary.btn-outline:hover {
  color: #FFF
}

.btn-primary.btn-outline:disabled {
  background: #ccc;
}
</style>
